import React, { useCallback, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Grid,
  GridItem,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  Skeleton,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { SaktiRkhDetailData } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';
import editIcon from '../../assets/icons/edit_icon.png';

interface SaktiRkhDetailModalProps {
  isOpen: boolean;
  loading: boolean;
  detailData: SaktiRkhDetailData | null;
  onClose: () => void;
  isEditMode: boolean;
  isSaving: boolean;
  isDirty: boolean;
  isValid: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDraftChange: (nextDraft: SaktiRkhDetailData) => void;
}

const MetricItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <VStack align="start" spacing={0}>
    <Text fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.500" letterSpacing="wide">
      {label}
    </Text>
    <Text fontWeight="semibold" fontSize="sm">
      {value || '-'}
    </Text>
  </VStack>
);

type SaktiLocation = SaktiRkhDetailData['locations'][number];
type SaktiForeman = SaktiRkhDetailData['foremen'][number];
type SaktiHarvester = SaktiRkhDetailData['harvesters'][number];
type SaktiHanca = SaktiRkhDetailData['hancas'][number];

const toNumberOrNaN = (value: number): number => (Number.isFinite(value) ? value : Number.NaN);

const toNullableString = (value: string): string | null => {
  const next = value.trim();
  return next ? next : null;
};

const SaktiRkhDetailModal: React.FC<SaktiRkhDetailModalProps> = ({
  isOpen,
  loading,
  detailData,
  onClose,
  isEditMode,
  isSaving,
  isDirty,
  isValid,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDraftChange,
}) => {
  const [activeTab, setActiveTab] = useState<'locations' | 'foremen' | 'harvesters' | 'hancas'>('locations');

  const isSaveDisabled = !isDirty || !isValid || isSaving;
  const isEditDisabled = !detailData || loading;
  const isCancelDisabled = isSaving;
  const isCloseDisabled = isSaving;

  const handleStartEdit = useCallback(() => {
    onStartEdit();
  }, [onStartEdit]);

  const handleCancelEdit = useCallback(() => {
    onCancelEdit();
  }, [onCancelEdit]);

  const handleSaveEdit = useCallback(() => {
    onSaveEdit();
  }, [onSaveEdit]);

  const handleClose = useCallback(() => {
    if (isCloseDisabled) {
      return;
    }

    onClose();
  }, [isCloseDisabled, onClose]);

  const updateDraft = useCallback(
    (updater: (current: SaktiRkhDetailData) => SaktiRkhDetailData) => {
      if (!detailData) {
        return;
      }

      const nextDraft = updater(detailData);
      onDraftChange(nextDraft);
    },
    [detailData, onDraftChange]
  );

  const updateRkhField = useCallback(
    <K extends keyof SaktiRkhDetailData['rkh']>(field: K, value: SaktiRkhDetailData['rkh'][K]) => {
      updateDraft((current) => ({
        ...current,
        rkh: {
          ...current.rkh,
          [field]: value,
        },
      }));
    },
    [updateDraft]
  );

  const updateLocationField = useCallback(
    <K extends keyof SaktiLocation>(index: number, field: K, value: SaktiLocation[K]) => {
      updateDraft((current) => ({
        ...current,
        locations: current.locations.map((location, currentIndex) =>
          currentIndex === index
            ? {
              ...location,
              [field]: value,
            }
            : location
        ),
      }));
    },
    [updateDraft]
  );

  const updateForemanField = useCallback(
    <K extends keyof SaktiForeman>(index: number, field: K, value: SaktiForeman[K]) => {
      updateDraft((current) => ({
        ...current,
        foremen: current.foremen.map((foreman, currentIndex) =>
          currentIndex === index
            ? {
              ...foreman,
              [field]: value,
            }
            : foreman
        ),
      }));
    },
    [updateDraft]
  );

  const updateHarvesterField = useCallback(
    <K extends keyof SaktiHarvester>(index: number, field: K, value: SaktiHarvester[K]) => {
      updateDraft((current) => ({
        ...current,
        harvesters: current.harvesters.map((harvester, currentIndex) =>
          currentIndex === index
            ? {
              ...harvester,
              [field]: value,
            }
            : harvester
        ),
      }));
    },
    [updateDraft]
  );

  const updateHancaField = useCallback(
    <K extends keyof SaktiHanca>(index: number, field: K, value: SaktiHanca[K]) => {
      updateDraft((current) => ({
        ...current,
        hancas: current.hancas.map((hanca, currentIndex) =>
          currentIndex === index
            ? {
              ...hanca,
              [field]: value,
            }
            : hanca
        ),
      }));
    },
    [updateDraft]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      scrollBehavior="inside"
      closeOnOverlayClick={!isCloseDisabled}
      closeOnEsc={!isCloseDisabled}
    >
      <ModalOverlay />
      <ModalContent borderRadius={SAKTI_UI_SIZE.cardRadius} overflow="hidden">
        <ModalHeader bg="customGold.500" color="white" fontSize={SAKTI_UI_SIZE.modalHeaderFont}>
          <Text as="span" fontWeight="bold" fontSize={SAKTI_UI_SIZE.modalHeaderFont}>
            Detail RKH
          </Text>
        </ModalHeader>
        <ModalCloseButton color="white" isDisabled={isCloseDisabled} />
        <ModalBody py={4}>
          <Skeleton isLoaded={!loading}>
            {!detailData ? (
              <Box py={6}>
                <Text textAlign="center" color="gray.500" fontSize="sm">
                  Detail tidak tersedia.
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={4}>
                <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={3}>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={3}>
                    <MetricItem label="RKH ID" value={detailData.rkh.id} />
                    {isEditMode ? (
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <VStack align="start" spacing={1}>
                          <Text fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.500" letterSpacing="wide">
                            RKH NUMBER
                          </Text>
                          <Input
                            size="sm"
                            borderRadius="xl"
                            borderColor="gray.400"
                            value={detailData.rkh.rkh_nbr}
                            onChange={(event) => updateRkhField('rkh_nbr', event.target.value)}
                            aria-label="RKH Number"
                          />
                        </VStack>
                      </GridItem>
                    ) : (
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <MetricItem label="RKH NUMBER" value={detailData.rkh.rkh_nbr} />
                      </GridItem>
                    )}
                    {isEditMode ? (
                      <VStack align="start" spacing={1}>
                        <Text fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.500" letterSpacing="wide">
                          RKH DATE
                        </Text>
                        <Input
                          size="sm"
                          borderRadius="xl"
                          borderColor="gray.400"
                          type="date"
                          value={detailData.rkh.rkh_date}
                          onChange={(event) => updateRkhField('rkh_date', event.target.value)}
                          aria-label="RKH Date"
                        />
                      </VStack>
                    ) : (
                      <MetricItem
                        label="RKH DATE"
                        value={dayjs(detailData.rkh.rkh_date).format('DD/MM/YYYY')}
                      />
                    )}
                    {isEditMode ? (
                      <VStack align="start" spacing={1}>
                        <Text fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.500" letterSpacing="wide">
                          STAGE
                        </Text>
                        <Select
                          size="sm"
                          borderRadius="xl"
                          borderColor="gray.400"
                          value={detailData.rkh.stage}
                          onChange={(event) => updateRkhField('stage', event.target.value)}
                          aria-label="Stage"
                        >
                          {[detailData.rkh.stage, 'DRAFT', 'READY', 'COMPLETE']
                            .filter((value, index, values) => values.indexOf(value) === index)
                            .map((value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ))}
                        </Select>
                      </VStack>
                    ) : (
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500" letterSpacing="wide">
                          STAGE
                        </Text>
                        <Badge colorScheme="green">{detailData.rkh.stage}</Badge>
                      </VStack>
                    )}
                    <>
                      <MetricItem label="COMPANY ID" value={String(detailData.rkh.company_id ?? '-')} />
                      <MetricItem label="ESTATE ID" value={String(detailData.rkh.estate_id ?? '-')} />
                      <MetricItem label="DIVISION ID" value={String(detailData.rkh.division_id ?? '-')} />
                    </>
                  </Grid>
                </Box>

                <HStack justify="space-between" align="flex-end" borderBottomWidth="1px" borderBottomColor="customGreen.500" mb={3} mt={4} flexWrap="wrap">
                  <HStack spacing={2} flexWrap="wrap">
                    {[
                      { id: 'locations', label: `Locations (${detailData.locations.length})` },
                      { id: 'foremen', label: `Foremen (${detailData.foremen.length})` },
                      { id: 'harvesters', label: `Harvesters (${detailData.harvesters.length})` },
                      { id: 'hancas', label: `Ancak (${detailData.hancas.length})` },
                    ].map((tab) => {
                      const isActive = activeTab === tab.id;
                      return (
                        <Button
                          key={tab.id}
                          size="sm"
                          variant="ghost"
                          borderBottomWidth="3px"
                          borderBottomColor={isActive ? 'customGreen.500' : 'transparent'}
                          borderRadius={0}
                          color={isActive ? 'customGreen.500' : 'gray.700'}
                          fontSize={SAKTI_UI_SIZE.tableCellFont}
                          fontWeight="bold"
                          onClick={() => setActiveTab(tab.id as 'locations' | 'foremen' | 'harvesters' | 'hancas')}
                        >
                          {tab.label}
                        </Button>
                      );
                    })}
                  </HStack>

                  <Box pb={1}>
                    {isEditMode ? (
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="gray"
                          onClick={handleCancelEdit}
                          isDisabled={isCancelDisabled}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={handleSaveEdit}
                          isDisabled={isSaveDisabled}
                          isLoading={isSaving}
                        >
                          Save
                        </Button>
                      </HStack>
                    ) : (
                      <Button
                        size="sm"
                        colorScheme="green"
                        variant="outline"
                        onClick={handleStartEdit}
                        isDisabled={isEditDisabled}
                        leftIcon={<img src={editIcon} width="14" height="14" alt="Edit" style={{ display: 'inline-block' }} />}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                </HStack>

                {activeTab === 'locations' && (
                  <Box>
                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflowX="auto">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>ID</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Block</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Carry Over</Th>
                            <Th isNumeric fontSize={SAKTI_UI_SIZE.tableHeaderFont}>
                              Harvest Area
                            </Th>
                            <Th isNumeric fontSize={SAKTI_UI_SIZE.tableHeaderFont}>
                              Target
                            </Th>
                            <Th isNumeric fontSize={SAKTI_UI_SIZE.tableHeaderFont}>
                              Est Weight
                            </Th>
                            <Th isNumeric fontSize={SAKTI_UI_SIZE.tableHeaderFont}>
                              Est Bunch
                            </Th>
                            <Th isNumeric fontSize={SAKTI_UI_SIZE.tableHeaderFont}>
                              Est HK
                            </Th>
                            <Th isNumeric fontSize={SAKTI_UI_SIZE.tableHeaderFont}>
                              Est Output
                            </Th>
                            <Th isNumeric fontSize={SAKTI_UI_SIZE.tableHeaderFont}>
                              Hanca Cnt
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {detailData.locations.map((location, index) => (
                            <Tr key={location.id}>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>{location.id}</Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Input
                                    size="sm"
                                    minW="100px"
                                    borderRadius="lg"
                                    borderColor="gray.400"
                                    value={location.block_code}
                                    onChange={(event) =>
                                      updateLocationField(index, 'block_code', event.target.value)
                                    }
                                    aria-label={`Location ${index + 1} Block`}
                                  />
                                ) : (
                                  location.block_code
                                )}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Switch
                                    size="sm"
                                    isChecked={location.is_carry_over}
                                    onChange={(event) =>
                                      updateLocationField(index, 'is_carry_over', event.target.checked)
                                    }
                                    aria-label={`Location ${index + 1} Carry Over`}
                                  />
                                ) : location.is_carry_over ? (
                                  'Yes'
                                ) : (
                                  'No'
                                )}
                              </Td>
                              <Td isNumeric fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <NumberInput
                                    size="sm"
                                    minW="100px"
                                    value={location.harvest_area}
                                    onChange={(_, valueAsNumber) =>
                                      updateLocationField(
                                        index,
                                        'harvest_area',
                                        toNumberOrNaN(valueAsNumber)
                                      )
                                    }
                                  >
                                    <NumberInputField
                                      borderRadius="lg"
                                      borderColor="gray.400"
                                      aria-label={`Location ${index + 1} Harvest Area`} />
                                  </NumberInput>
                                ) : (
                                  location.harvest_area
                                )}
                              </Td>
                              <Td isNumeric fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <NumberInput
                                    size="sm"
                                    minW="100px"
                                    value={location.harvest_area_target}
                                    onChange={(_, valueAsNumber) =>
                                      updateLocationField(
                                        index,
                                        'harvest_area_target',
                                        toNumberOrNaN(valueAsNumber)
                                      )
                                    }
                                  >
                                    <NumberInputField
                                      borderRadius="lg"
                                      borderColor="gray.400"
                                      aria-label={`Location ${index + 1} Harvest Target`} />
                                  </NumberInput>
                                ) : (
                                  location.harvest_area_target
                                )}
                              </Td>
                              <Td isNumeric fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <NumberInput
                                    size="sm"
                                    minW="100px"
                                    value={location.est_weight}
                                    onChange={(_, valueAsNumber) =>
                                      updateLocationField(
                                        index,
                                        'est_weight',
                                        toNumberOrNaN(valueAsNumber)
                                      )
                                    }
                                  >
                                    <NumberInputField
                                      borderRadius="lg"
                                      borderColor="gray.400"
                                      aria-label={`Location ${index + 1} Est Weight`} />
                                  </NumberInput>
                                ) : (
                                  location.est_weight
                                )}
                              </Td>
                              <Td isNumeric fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <NumberInput
                                    size="sm"
                                    minW="100px"
                                    value={location.est_bunch}
                                    onChange={(_, valueAsNumber) =>
                                      updateLocationField(
                                        index,
                                        'est_bunch',
                                        toNumberOrNaN(valueAsNumber)
                                      )
                                    }
                                  >
                                    <NumberInputField
                                      borderRadius="lg"
                                      borderColor="gray.400"
                                      aria-label={`Location ${index + 1} Est Bunch`} />
                                  </NumberInput>
                                ) : (
                                  location.est_bunch
                                )}
                              </Td>
                              <Td isNumeric fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <NumberInput
                                    size="sm"
                                    minW="100px"
                                    value={location.est_hk}
                                    onChange={(_, valueAsNumber) =>
                                      updateLocationField(index, 'est_hk', toNumberOrNaN(valueAsNumber))
                                    }
                                  >
                                    <NumberInputField
                                      borderRadius="lg"
                                      borderColor="gray.400"
                                      aria-label={`Location ${index + 1} Est HK`} />
                                  </NumberInput>
                                ) : (
                                  location.est_hk
                                )}
                              </Td>
                              <Td isNumeric fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <NumberInput
                                    size="sm"
                                    minW="100px"
                                    value={location.est_output}
                                    onChange={(_, valueAsNumber) =>
                                      updateLocationField(
                                        index,
                                        'est_output',
                                        toNumberOrNaN(valueAsNumber)
                                      )
                                    }
                                  >
                                    <NumberInputField
                                      borderRadius="lg"
                                      borderColor="gray.400"
                                      aria-label={`Location ${index + 1} Est Output`} />
                                  </NumberInput>
                                ) : (
                                  location.est_output
                                )}
                              </Td>
                              <Td isNumeric fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <NumberInput
                                    size="sm"
                                    minW="100px"
                                    value={location.hanca_cnt}
                                    onChange={(_, valueAsNumber) =>
                                      updateLocationField(
                                        index,
                                        'hanca_cnt',
                                        toNumberOrNaN(valueAsNumber)
                                      )
                                    }
                                  >
                                    <NumberInputField
                                      borderRadius="lg"
                                      borderColor="gray.400"
                                      aria-label={`Location ${index + 1} Hanca Count`} />
                                  </NumberInput>
                                ) : (
                                  location.hanca_cnt
                                )}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {activeTab === 'foremen' && (
                  <Box>
                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflowX="auto">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>ID</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Foreman Group</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Foreman</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Foreman 1</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Kerani Harvest</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Kutip Required</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {detailData.foremen.map((foreman, index) => (
                            <Tr key={foreman.id}>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>{foreman.id}</Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {foreman.foreman_group_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {foreman.foreman_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {foreman.foreman1_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {foreman.kerani_harvest_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Switch
                                    isChecked={foreman.is_kutip_required}
                                    onChange={(event) =>
                                      updateForemanField(
                                        index,
                                        'is_kutip_required',
                                        event.target.checked
                                      )
                                    }
                                    aria-label={`Foreman ${index + 1} Kutip Required`}
                                  />
                                ) : foreman.is_kutip_required ? (
                                  'Yes'
                                ) : (
                                  'No'
                                )}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {activeTab === 'harvesters' && (
                  <Box>
                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflowX="auto">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>ID</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Foreman ID</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Emp ID</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>NIP</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>FP ID</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Name</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Job Lv</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Job</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Attendance</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Time In</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Time Out</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Kutip</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Asistensi</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {detailData.harvesters.map((harvester, index) => (
                            <Tr key={harvester.id}>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>{harvester.id}</Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {harvester.foreman_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {harvester.emp_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Input
                                    size="sm"
                                    minW="130px"
                                    value={harvester.nip}
                                    onChange={(event) =>
                                      updateHarvesterField(index, 'nip', event.target.value)
                                    }
                                    aria-label={`Harvester ${index + 1} NIP`}
                                  />
                                ) : (
                                  harvester.nip
                                )}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Input
                                    size="sm"
                                    minW="100px"
                                    value={harvester.fp_id}
                                    onChange={(event) =>
                                      updateHarvesterField(index, 'fp_id', event.target.value)
                                    }
                                    aria-label={`Harvester ${index + 1} FP ID`}
                                  />
                                ) : (
                                  harvester.fp_id
                                )}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Input
                                    size="sm"
                                    minW="160px"
                                    value={harvester.name}
                                    onChange={(event) =>
                                      updateHarvesterField(index, 'name', event.target.value)
                                    }
                                    aria-label={`Harvester ${index + 1} Name`}
                                  />
                                ) : (
                                  harvester.name
                                )}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {harvester.job_level_name || harvester.job_level_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {harvester.job_name || harvester.job_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {harvester.attendance_type_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Input
                                    size="sm"
                                    minW="120px"
                                    type="time"
                                    value={harvester.time_in ?? ''}
                                    onChange={(event) =>
                                      updateHarvesterField(index, 'time_in', toNullableString(event.target.value))
                                    }
                                    aria-label={`Harvester ${index + 1} Time In`}
                                  />
                                ) : (
                                  harvester.time_in || '-'
                                )}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Input
                                    size="sm"
                                    minW="120px"
                                    type="time"
                                    value={harvester.time_out ?? ''}
                                    onChange={(event) =>
                                      updateHarvesterField(index, 'time_out', toNullableString(event.target.value))
                                    }
                                    aria-label={`Harvester ${index + 1} Time Out`}
                                  />
                                ) : (
                                  harvester.time_out || '-'
                                )}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Switch
                                    isChecked={harvester.is_kutip_required}
                                    onChange={(event) =>
                                      updateHarvesterField(
                                        index,
                                        'is_kutip_required',
                                        event.target.checked
                                      )
                                    }
                                    aria-label={`Harvester ${index + 1} Kutip Required`}
                                  />
                                ) : harvester.is_kutip_required ? (
                                  'Yes'
                                ) : (
                                  'No'
                                )}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <Switch
                                    isChecked={harvester.is_asistensi}
                                    onChange={(event) =>
                                      updateHarvesterField(index, 'is_asistensi', event.target.checked)
                                    }
                                    aria-label={`Harvester ${index + 1} Asistensi`}
                                  />
                                ) : harvester.is_asistensi ? (
                                  'Yes'
                                ) : (
                                  'No'
                                )}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {activeTab === 'hancas' && (
                  <Box>
                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflowX="auto">
                      <Table size="sm">
                        <Thead>
                          <Tr>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>ID</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Hanca Nbr</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Location ID</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Harvester ID</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Foreman ID</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {detailData.hancas.map((hanca, index) => (
                            <Tr key={hanca.id}>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>{hanca.id}</Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {isEditMode ? (
                                  <NumberInput
                                    size="sm"
                                    value={hanca.hanca_nbr}
                                    onChange={(_, valueAsNumber) =>
                                      updateHancaField(index, 'hanca_nbr', toNumberOrNaN(valueAsNumber))
                                    }
                                  >
                                    <NumberInputField aria-label={`Hanca ${index + 1} Number`} />
                                  </NumberInput>
                                ) : (
                                  hanca.hanca_nbr
                                )}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {hanca.location_id}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {hanca.harvester_id || '-'}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {hanca.foreman_id || '-'}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}
              </VStack>
            )}
          </Skeleton>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SaktiRkhDetailModal;
